import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldWarning, ArrowsClockwise, Trash, WarningCircle, CircleNotch, X, Cube } from '@phosphor-icons/react';

const HerokuMasterWipe = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAcc, setSelectedAcc] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncData, setSyncData] = useState(null);
  
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    fetchAccounts(true);
    const interval = setInterval(() => fetchAccounts(false), 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchAccounts = async (isInitialLoad = false) => {
    try {
      const res = await axios.get(`/api/heroku/accounts`, { withCredentials: true });
      setAccounts(res.data);
      if (isInitialLoad && res.data.length > 0) {
        setSelectedAcc(res.data[0].id);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSync = async () => {
    if (!selectedAcc) return toast.error("Select an account first");
    setSyncing(true);
    setSyncData(null);
    try {
      const res = await axios.get(`/api/heroku/accounts/${selectedAcc}/sync`, { withCredentials: true });
      setSyncData(res.data);
      toast.success("Heroku Sync Complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to sync with Heroku");
    } finally {
      setSyncing(false);
    }
  };

  const executeWipe = async () => {
    if (confirmText !== 'WIPE') return toast.error("You must type WIPE exactly to confirm.");
    if (!syncData) return;

    try {
      await axios.post(`/api/heroku/accounts/${selectedAcc}/wipe`, {
        apps_items: syncData.apps_items
      }, { withCredentials: true });
      
      toast.success("Master Wipe Initiated! This will run in the background.");
      setShowConfirm(false);
      setConfirmText('');
      setSyncData(null);
      fetchAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start wipe");
    }
  };

  const currentAccObj = accounts.find(a => a.id === selectedAcc);
  const isWiping = currentAccObj?.wipe_status === 'wiping';

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
      <div>
        <h1 className="text-4xl font-black text-red-500 mb-2 flex items-center gap-3">
          <ShieldWarning size={40} /> MASTER WIPE
        </h1>
        <p className="text-slate-400 font-mono">Sync directly with Heroku and permanently obliterate all Apps.</p>
      </div>

      <div className="bg-card border border-red-500/20 p-8 max-w-2xl space-y-8 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-red-500/5 blur-3xl rounded-full pointer-events-none" />

        <div className="space-y-2 relative z-10">
          <label className="text-xs uppercase tracking-widest text-slate-500 font-bold block">1. Select Heroku Account</label>
          <select 
            value={selectedAcc} 
            onChange={(e) => { setSelectedAcc(e.target.value); setSyncData(null); }}
            className="w-full bg-[#0A0B0E] border border-white/10 p-4 text-white focus:outline-none focus:border-red-500 appearance-none rounded-none text-lg"
          >
            <option value="">-- Choose Account --</option>
            {accounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>
        </div>

        <div className="relative z-10">
          {isWiping ? (
            <div className="bg-red-500/10 border border-red-500/30 p-6 flex flex-col items-center justify-center text-center space-y-4">
              <CircleNotch size={48} className="text-red-500 animate-spin" />
              <div>
                <h3 className="text-red-500 font-bold text-xl uppercase tracking-widest">Wipe in Progress</h3>
                <p className="text-slate-400 font-mono text-sm mt-2">The backend is currently annihilating all apps in Heroku.</p>
              </div>
            </div>
          ) : (
            <button 
              onClick={handleSync}
              disabled={syncing || !selectedAcc}
              className="w-full bg-transparent border-2 border-[#8b60ed] text-[#8b60ed] px-8 py-4 font-black uppercase tracking-wider hover:bg-[#8b60ed] hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {syncing ? <CircleNotch size={20} className="animate-spin" /> : <ArrowsClockwise size={20} />}
              {syncing ? 'SYNCING FROM HEROKU...' : 'SYNC USAGE & ANALYZE'}
            </button>
          )}
        </div>

        {syncData && !isWiping && (
          <div className="space-y-6 relative z-10 animate-in fade-in duration-300">
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-[#0A0B0E] border border-white/10 p-6 flex flex-col items-center justify-center text-center">
                <Cube size={32} className="text-slate-500 mb-2" />
                <div className="text-4xl font-black text-white">{syncData.apps_count}</div>
                <div className="text-xs uppercase tracking-widest text-slate-500 mt-2 font-bold">Heroku Apps</div>
              </div>
            </div>

            <div className="bg-red-500/10 border border-red-500/20 p-4 flex items-start gap-3">
              <WarningCircle size={24} className="text-red-500 shrink-0 mt-0.5" weight="fill" />
              <div className="text-sm text-red-200 leading-relaxed font-mono">
                WARNING: Executing a Master Wipe will permanently delete <strong className="text-white">ALL {syncData.apps_count} Apps</strong> from this Heroku account, regardless of whether they were created by this application.
              </div>
            </div>

            <button 
              onClick={() => setShowConfirm(true)}
              disabled={syncData.apps_count === 0}
              className="w-full bg-red-600 text-white px-8 py-4 font-black uppercase tracking-wider hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Trash size={20} /> INITIATE MASTER WIPE
            </button>
          </div>
        )}
      </div>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-[#0A0B0E] border border-red-500 p-8 w-full max-w-lg relative animate-in zoom-in-95 duration-200 shadow-[0_0_50px_rgba(239,68,68,0.2)]">
            <button onClick={() => {setShowConfirm(false); setConfirmText('');}} className="absolute top-4 right-4 text-slate-500 hover:text-white">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-black mb-2 text-red-500 uppercase">Confirm Annihilation</h2>
            <p className="text-slate-400 font-mono text-sm mb-6">Type <strong className="text-white">WIPE</strong> in the box below to authorize the deletion of {syncData?.apps_count} resources.</p>
            
            <input 
              type="text" 
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="WIPE"
              className="w-full bg-black border-2 border-red-500/50 p-4 text-white text-center font-black tracking-widest focus:outline-none focus:border-red-500 mb-6 uppercase"
            />

            <button 
              onClick={executeWipe}
              disabled={confirmText !== 'WIPE'}
              className="w-full bg-red-600 text-white font-black py-4 uppercase tracking-widest hover:bg-red-700 transition-colors disabled:opacity-50 disabled:bg-red-900"
            >
              NUKE EVERYTHING
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HerokuMasterWipe;